import { Component, OnInit } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { EditableComponent } from '../../../../shared/components/interfaces/editable.component';
import { ListeableComponent } from '../../../../shared/components/interfaces/listeable.component';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { MiscellaneousUtils } from '../../../../shared/utils/miscellaneous.utils';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';
import { AssetTypeUIMessages } from '../../model/asset-type-ui.messages';
import { AssetTypeModel } from '../../model/asset-type.model';
import { AssetTypeService } from '../../service/asset-type.service';
import { AssetTypeAddDialogComponent } from '../asset-type-add-dialog/asset-type-add-dialog.component';

@Component({
  selector: 'asset-type-list',
  templateUrl:
    'console/app-js/modules/assetType/components/asset-type-list/asset-type-list.component.html'
})
export class AssetTypeListComponent extends APIOperableComponent
  implements ListeableComponent, EditableComponent, OnInit {
  protected assetTypesData: Array<AssetTypeModel>;
  protected data: Array<AssetTypeModel> = [];
  protected originalData: Array<AssetTypeModel> = [];
  protected allAssetTypes: Array<AssetTypeModel> = [];
  protected gridView: GridDataResult;
  protected pageSize = 100;
  protected skip = 0;
  protected multiple = false;
  protected allowUnsort = true;
  protected sort: SortDescriptor[] = [
    {
      field: 'name',
      dir: 'asc'
    }
  ];

  protected assetType: Array<string> = [
    'Active and Inactive',
    'Active',
    'Inactive'
  ];
  protected assetTypeStatus: Array<string>;
  protected loading = false;

  protected assetTypeInputData: string;
  protected assetStatusInputData: boolean;
  protected customInputType: any;

  constructor(
    protected progressService: NgProgress,
    protected toastr: ToastrService,
    protected authService: AuthService,
    protected dialogService: UIDialogService,
    protected assetTypeService: AssetTypeService
  ) {
    super(progressService, toastr, authService);

    this.onSearch();
  }

  ngOnInit() {
    this.assetTypeStatus = this.assetType.slice();
  }

  /**
   * Load all asset types.
   */
  onSearch(): void {
    this.startProgress();
    this.loading = true;
    this.assetTypeService.getAssetTypes().subscribe(
      result => {
        this.data = result;
        this.originalData = MiscellaneousUtils.cloneArray(this.data);
        this.assetTypesData = this.originalData.map(assetType => {
          return {
            id: assetType.id,
            name: assetType.name,
            createdBy: assetType.createdBy,
            isActive: assetType.isActive,
            updatedBy: assetType.updatedBy
          };
        });
        this.allAssetTypes = this.assetTypesData.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'name')
        );
        this.assetTypesData = this.allAssetTypes;
        this.loadProducts();
        this.finishProgress();
        this.loading = false;
      },
      err => this.onAPIError(AssetTypeUIMessages.LOAD_ASSET_TYPE_LIST_ERROR)
    );
    this.customInputType = this.assetType[0];
  }

  /**
   * Filter data.
   */
  protected handleFilter(value) {
    if (value.length >= 1) {
      this.assetTypeStatus = this.assetType.filter(
        s => s.toLowerCase().indexOf(value.toLowerCase()) !== -1
      );
    } else {
      this.assetTypeStatus = this.assetType.slice();
    }
  }

  /**
   * Reload all asset types after added new asset type.
   */
  onSave(): void {
    // Silence is golden.
  }

  onCancel(param: any): void {
    // Silence is golden.
  }

  /**
   * Dirty check of data.
   */
  isDirty(): boolean {
    return false;
  }

  /**
   * Filter data on selection of active or inactive.
   */
  protected selectionChange(value: any): void {
    // filter our data
    let temp: any;
    if (value && value !== 'Active and Inactive') {
      value = value === 'Active' ? true : false;
      this.assetStatusInputData = value;
      temp = this.allAssetTypes.filter(row => {
        return (
          row.isActive === value &&
          (this.assetTypeInputData !== undefined
            ? row.name
                .toLowerCase()
                .indexOf(this.assetTypeInputData.toLowerCase()) !== -1 ||
              !this.assetTypeInputData
            : true)
        );
      });
    } else {
      this.assetStatusInputData = undefined;
      temp = this.allAssetTypes.filter(row => {
        return this.assetTypeInputData !== undefined
          ? row.name
              .toLowerCase()
              .indexOf(this.assetTypeInputData.toLowerCase()) !== -1 ||
              !this.assetTypeInputData
          : true;
      });
    }
    this.assetTypesData = temp;
    this.skip = 0;
    this.loadProducts();
  }

  /**
   * On Filter input text data.
   */
  protected onFilterData(assetType: any): void {
    let value = assetType.target.value.toLocaleLowerCase();
    let temp: any;
    // filter our data
    if (this.assetStatusInputData !== undefined) {
      temp = this.allAssetTypes.filter(row => {
        return (
          (row.name.toLowerCase().indexOf(value) !== -1 || !value) &&
          row.isActive === this.assetStatusInputData
        );
      });
    } else {
      temp = this.allAssetTypes.filter(row => {
        return row.name.toLowerCase().indexOf(value) !== -1 || !value;
      });
    }
    this.assetTypesData = temp;
    this.skip = 0;
    this.loadProducts();
  }

  /**
   * Load data for page change.
   */
  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.loadProducts();
  }

  /**
   * Load data on grid view.
   */
  private loadProducts(): void {
    this.gridView = {
      data: this.assetTypesData.slice(this.skip, this.skip + this.pageSize),
      total: this.assetTypesData.length
    };
  }

  /**
   * Sort data on sort change.
   */
  protected sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.sortProducts();
  }

  /**
   * Show sorted data on grid view.
   */
  private sortProducts(): void {
    this.gridView = {
      data: orderBy(this.assetTypesData, this.sort),
      total: this.assetTypesData.length
    };
  }

  /**
   * Open dialog box to add new asset type.
   */
  onAddItem(): void {
    this.dialogService
      .open(AssetTypeAddDialogComponent, [
        { provide: 'assetTypeModel', useValue: null }
      ])
      .then(result => {
        if (result) {
          this.onSearch();
          this.onReset();
        }
      })
      .catch(result => {
        // Silence is golden.
      });
  }

  /**
   * onChange switch value.
   */
  valueChanged(dataItem): void {
    // dataItem.isActive === true ? this.onDeleteItem(dataItem) : this.onEditItem(dataItem);
    if (dataItem.isActive) {
      dataItem.isActive = false;
      this.onDeleteItem(dataItem);
    } else {
      dataItem.isActive = true;
      this.onEditItem(dataItem);
    }
  }

  /**
   * Change inactive item to active.
   */
  onEditItem(assetTypeModel: AssetTypeModel): void {
    this.startProgress();
    this.assetTypeService.updateAssetType(assetTypeModel).subscribe(
      result => {
        if (result.responseCode === 200) {
          this.onAPISuccess(AssetTypeUIMessages.EDIT_ASSET_TYPE_SUCCESS);
          assetTypeModel.isActive = true;
          this.selectionChange(this.customInputType);
          this.finishProgress();
        } else {
          assetTypeModel.isActive = false;
          this.onAPIError(AssetTypeUIMessages.EDIT_ASSET_TYPE_ERROR);
        }
      },
      err => {
        assetTypeModel.isActive = false;
        this.onAPIError(AssetTypeUIMessages.EDIT_ASSET_TYPE_ERROR);
      }
    );
  }

  /**
   * Change active item to inactive.
   */
  onDeleteItem(assetTypeModel: AssetTypeModel): void {
    this.startProgress();
    this.assetTypeService.deleteAssetType(assetTypeModel).subscribe(
      result => {
        if (result.responseCode === 200) {
          this.onAPISuccess(AssetTypeUIMessages.EDIT_ASSET_TYPE_SUCCESS);
          assetTypeModel.isActive = false;
          this.selectionChange(this.customInputType);
          this.finishProgress();
        } else {
          assetTypeModel.isActive = true;
          this.onAPIError(AssetTypeUIMessages.EDIT_ASSET_TYPE_ERROR);
        }
      },
      err => {
        assetTypeModel.isActive = true;
        this.onAPIError(AssetTypeUIMessages.EDIT_ASSET_TYPE_ERROR);
      }
    );
  }

  onDeleteItems(): void {
    // Silence is golden.
  }

  onReset(): void {
    this.assetTypeInputData = undefined;
    this.customInputType = this.assetType[0];
    this.selectionChange(this.assetType[0]);
  }

  onFullscreenMode(): void {
    // Silence is golden.
  }
}
